import * as yup from "yup";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Images } from "../../../../../config/images";
import TopImage from "../../../common/TopImage";
import CustomInput from "../../../../common/CustomInput";
import CustomSelect from "../../../../common/CustomSelect";
import SuccessModal from "../../../../common/SuccessModal";
import "../../../SchoolAdmin/Teacher/Teacher.css";
import "../../../../../common/styles/table.css";
import { useState, useEffect } from "react";
import Avatar from "react-avatar";
import BackButton from "../../../../common/BackButton";
import { Button } from "react-bootstrap";
import { useGetMemberDetailById } from "../../../../../hooks/organization/members/useGetMemberDetailById";
import { useUpdateMemberDetail } from "../../../../../hooks/organization/members/useUpdateMemberDetail";
import { useGetCreatableRoles } from "../../../../../hooks/organization/members/useGetCreatableRoles";
import CommonLoader from "../../../../common/CommonLoader";
import Loader from "../../../../common/Loader";

const EditMember = () => {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    member,
    loading: fetchLoading,
    error: fetchError,
    getMemberDetailById,
  } = useGetMemberDetailById();

  const {
    updateMemberDetail,
    loading: updateLoading,
    error: updateError,
  } = useUpdateMemberDetail();

  const {
    roles,
    loading: rolesLoading,
    error: rolesError,
    getCreatableRoles,
  } = useGetCreatableRoles();

  useEffect(() => {
    if (memberId) {
      getMemberDetailById({ memberId });
    }
    getCreatableRoles();
  }, [memberId]);

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .required("First name is required")
      .max(150, "First name must be at most 150 characters")
      .matches(/^[A-Za-z ]+$/, "First name must contain only letters"),
    lastName: yup
      .string()
      .required("Last name is required")
      .max(150, "Last name must be at most 150 characters")
      .matches(/^[A-Za-z ]+$/, "Last name must contain only letters"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required")
      .max(150, "Email must be at most 150 characters"),
    role_slug: yup.string().required("Role is required"),
  });

  const initialValues = member
    ? {
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      email: member.email || "",
      role_slug: member.role?.slug || "",
    }
    : { firstName: "", lastName: "", email: "", role_slug: "" };

  const handleSubmit = async (values) => {
    await updateMemberDetail({
      memberId,
      updatedData: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        role_slug: values.role_slug,
      },
      onSuccess: () => {
        setShowSuccessModal(true);
      },
    });
  };

  const handleGoToMemberList = () => {
    setShowSuccessModal(false);
    navigate("/school-admin/settings/members");
  };

  const handleContinueEditing = () => {
    setShowSuccessModal(false);
  };

  // Get full name for display
  const fullName = member
    ? [member.firstName, member.lastName].filter(Boolean).join(" ")
    : "";


  if (fetchLoading && member === undefined) {
    return (
      <div>
        <div className="pb-2">
          <BackButton />
        </div>
        <TopImage imageSrc={Images.TopImage} text="Edit Member" />
        <Loader message="Loading Member Details..." />

      </div>
    );
  }

  if (fetchError) {
    return (
      <div>
        <div className="pb-2">
          <BackButton />
        </div>
        <TopImage imageSrc={Images.TopImage} text="Edit Member" />
        <div className="custom-card mt-4">
          <div className="text-center py-4 text-danger">{fetchError}</div>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div>
        <div className="pb-2">
          <BackButton />
        </div>
        <TopImage imageSrc={Images.TopImage} text="Edit Member" />
        <div className="custom-card mt-4">
          <div className="text-center py-4">Member not found.</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="pb-2">
        <BackButton />
      </div>

      <TopImage imageSrc={Images.TopImage} text="Edit Member" />

      <div className="custom-card mt-4">
        {/* Member Header with Avatar */}
        <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom">
          <Avatar
            name={fullName}
            round="50%"
            size="50"
            color="#E0F7FA"
            fgColor="#00838F"
          />
          <div>
            <div
              style={{
                fontWeight: "600",
                fontSize: "18px",
                color: "var(--text-color)",
              }}
            >
              {fullName}
            </div>
            <div style={{ fontSize: "14px", color: "#6c757d" }}>
              {member.email}
            </div>
          </div>
        </div>

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <CustomInput
                label="First Name"
                name="firstName"
                type="text"
                placeholder="Enter first name"
                className="custom-primary-input"
                layout="stacked"
              />

              <CustomInput
                label="Last Name"
                name="lastName"
                type="text"
                placeholder="Enter last name"
                className="custom-primary-input"
                layout="stacked"
              />

              <CustomInput
                label="Email"
                name="email"
                type="email"
                placeholder="Enter email address"
                className="custom-primary-input"
                layout="stacked"
              />

              <CustomSelect
                label="Role"
                name="role_slug"
                className="custom-primary-input"
                layout="stacked"
                disabled={rolesLoading}
              >
                <option value="">
                  {rolesLoading ? "Loading roles..." : "Select Role"}
                </option>
                {roles?.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </CustomSelect>

              <div>
                {(updateError || rolesError) && (
                  <div className="custom-error pb-2">
                    {updateError || rolesError}
                  </div>
                )}

                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={isSubmitting || updateLoading || rolesLoading}
                >
                  {isSubmitting || updateLoading ? "Saving..." : "Save"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Success Modal */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title="Success"
        message="Member details have been updated successfully."
        primaryButtonText="Continue Editing"
        secondaryButtonText="Member List"
        onPrimaryClick={handleContinueEditing}
        onSecondaryClick={handleGoToMemberList}
      />
    </div>
  );
};

export default EditMember;
